<template>
  <div :id="uid" class="comp-modal" :class="{ visible }">
    <div class="click-catcher" @click="close"></div>
    <div ref="container" class="container" tabindex="0">
      <div class="content">
        <span v-if="title" class="modal-title">{{ title }}</span>
        <slot>
        </slot>
      </div>
      <div v-if="actionButtons" class="actions">
        <Button
          v-for="action in actionButtons"
          :key="action.key"
          :icon="action.icon"
          class="centered"
          :class="action.class"
          @click="handleActionClick(action.value)">
          {{ action.label }}
        </Button>
      </div>
    </div>
  </div>
</template>

<script>
  import Vue from 'vue';
  import Button from '@/components/ui/Button';
  import Key from '@/objects/KeyboardKeys';
  import Event from '@/objects/DomEvents';

  //----------------------------------
  // Private Properties
  //----------------------------------
  let uid = 0;

  export default {
    name: 'Modal',
    components: {
      Button,
    },
    //---------------------------------------------------
    //
    //  Properties
    //
    //---------------------------------------------------
    props: {
      id: {
        type: String,
        default: null,
      },
      title: {
        type: String,
        default: null,
      },
      actions: {
        type: Array,
        default: null,
      },
      formAction: {
        type: String,
        default: null,
      },
    },
    //---------------------------------------------------
    //
    //  Data model
    //
    //---------------------------------------------------
    data() {
      uid += 1;
      return {
        uid: this.id || `modal-${uid}`,
        visible: false,
      };
    },
    //---------------------------------------------------
    //
    //  Watch Properties
    //
    //---------------------------------------------------
    watch: {},
    //---------------------------------------------------
    //
    //  Computed Properties
    //
    //---------------------------------------------------
    computed: {
      actionButtons() {
        const out = [];
        const actions = this.actions || [];
        const numActions = actions.length;
        actions.forEach((action, index) => {
          const btnData = {
            ...action,
          };
          if (!btnData.class) {
            btnData.class = (index + 1 < numActions) ? 'gray' : 'red';
          }
          out.push(btnData);
        });
        return out.length > 0 ? out : null;
      },
    },
    //---------------------------------------------------
    //
    //  Filter Properties
    //
    //---------------------------------------------------
    filters: {},
    //---------------------------------------------------
    //
    //  Vue Lifecycle
    //
    //---------------------------------------------------
    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // render(h) { return h(); },
    mounted() {
      document.body.appendChild(this.$el);
    },
    // beforeUpdate() {},
    // updated() {},
    beforeDestroy() {
      if (this.$el.parentNode) {
        this.$el.parentNode.removeChild(this.$el);
      }
    },
    // destroyed() {},
    //---------------------------------------------------
    //
    //  Methods
    //
    //---------------------------------------------------
    methods: {
      show() {
        window.addEventListener(Event.KEY_DOWN, this.handleKeyboardEvents);
        this.visible = true;
        Vue.nextTick(() => {
          const form = (this.$slots.default || []).find(vnode => vnode.tag === 'form');
          const input = this.$el.querySelector('input');
          if (form) {
            form.elm.addEventListener('submit', this.handleFormSubmit);
          } else {
            this.$refs.container.focus();
          }

          if (input) {
            input.focus();
          }
        });
      },
      close() {
        window.removeEventListener(Event.KEY_DOWN, this.handleKeyboardEvents);
        this.visible = false;
      },
      //----------------------------------
      // Event Handler
      //----------------------------------
      handleActionClick(value) {
        this.$emit('action', value);
      },
      handleFormSubmit(evt) {
        evt.preventDefault();
        evt.stopPropagation();
        if (this.formAction) {
          this.$emit('action', this.formAction);
        }
      },
      handleKeyboardEvents(evt) {
        if ([Key.ESCAPE, Key.ESCAPE_ALT].includes(evt.key)) {
          this.close();
        }
      },
    },
  };
</script>

<style lang="scss">
  @import '~scss/variables';

  $default-grid-pixels: 8px;

  .comp-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 100;
    background: rgba(0, 0, 0, .8);
    display: inline-flex;
    justify-content: center;
    opacity: 0;
    pointer-events: none;

    &.visible {
      opacity: 1;
      pointer-events: all;
      transition: opacity $default-transition;
      .container {
        opacity: 1;
        transition: opacity $default-transition 0.3s;
      }
    }

    .click-catcher {
      position: absolute;
      z-index: 1;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: $color-black;
      opacity: 0;
    }

    .container {
      position: relative;
      z-index: 2;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-self: center;
      background: $color-white;
      min-height: $default-grid-pixels * 26;
      width: $default-grid-pixels * 68;
      padding: $default-grid-pixels * 2;
      box-shadow: 0 0 7px $color-black;
      opacity: 0;
      outline: 0;

      &.align-top {
        align-self: flex-start;
        margin-top: 50%;
      }

      .content {
        font-family: inherit;
        color: $color-black;
        & > * {
          margin: #{$default-grid-pixels * 2} 0 0 0;
        }

        span.modal-title {
          margin-top: 0;
          display: block;
          text-transform: uppercase;
          font-weight: bold;

          & + p {
            margin-top: $default-grid-pixels;
          }
        }

        p {
          font-size: 1.6rem;
          line-height: 1.5;
        }


        hr {
          margin: 0;
          border: 0;
          margin-left: - #{$default-grid-pixels * 2};
          height: 1px;
          background: transparent linear-gradient(0deg, transparent 0%, $color-gainsboro 50%);
          width: calc(100% + #{$default-grid-pixels * 4});
        }
      }

      .actions {
        margin-top: $default-grid-pixels * 4;
        display: flex;
        justify-content: flex-end;

        button {
          min-width: $default-grid-pixels * 15;
          margin-right: $default-grid-pixels * 2;
          &:last-of-type {
            margin-right: 0;
          }
        }
      }
    }
  }
</style>
